const styleWithUtilityClasses = {
    name: "Styling with Utility Classes",
    path: "styling-with-utility-classes",
    title: "Styling with Utility Classes",
    description: "Building complex components from a constrained set of primitive utilities.",
    heading: false,
    exampleData: [
        {
            "title": "Overview",
            "description": "",
            "moreDescription": "You style things with Unicss by combining many single-purpose presentational classes (utility classes) directly in your markup:",
            "parentClass": "!bg-none !border-0",
            "example": {
                "element": "div",
                "className": "relative",
                "code": `<div class="mx-auto flex max-w-sm items-center gap-x-4 rounded-xl bg-color-white p-6 shadow-lg outline outline-color-black/5 dark:bg-color-slate-800 dark:shadow-none dark:-outline-offset-1 dark:outline-color-white/10">
  <img class="size-12 shrink-0" src="/img/logo.svg" alt="Logo" />
  <div>
    <div class="text-xl font-medium text-color-black dark:text-color-white">Name</div>
    <p class="text-color-gray-500 dark:text-color-gray-400">You have a new message!</p>
  </div>
</div>`
            },
            "moreexample": `<div class="p-6 text-color-gray-800 dark:text-color-gray-300 space-y-6">
  <h2 class="text-sm font-semibold">For example, in the UI above we’ve used:</h2>
  <ul class="list-disc pl-6 space-y-2">
    <li>
      The <span class="font-medium">display and padding</span> utilities
      (<code class=" px-1 rounded">flex</code>, <code class=" px-1 rounded">shrink-0</code>, and <code class=" px-1 rounded">p-6</code>) to control the overall layout
    </li>
    <li>
      The <span class="font-medium">max-width and margin</span> utilities
      (<code class=" px-1 rounded">max-w-sm</code> and <code class=" px-1 rounded">mx-auto</code>) to constrain the card width and center it horizontally
    </li>
    <li>
      The <span class="font-medium">background-color, border-radius, and box-shadow</span> utilities
      (<code class=" px-1 rounded">bg-white</code>, <code class=" px-1 rounded">rounded-xl</code>, and <code class=" px-1 rounded">shadow-lg</code>) to style the card’s appearance
    </li>
    <li>
      The <span class="font-medium">width and height</span> utilities
      (<code class=" px-1 rounded">size-12</code>) to set the width and height of the logo image
    </li>
    <li>
      The <span class="font-medium">gap</span> utilities
      (<code class=" px-1 rounded">gap-x-4</code>) to handle the spacing between the logo and the text
    </li>
    <li>
      The <span class="font-medium">font-size, color, and font-weight</span> utilities
      (<code class=" px-1 rounded">text-xl</code>, <code class=" px-1 rounded">text-black</code>, <code class=" px-1 rounded">font-medium</code>) to style the card text
    </li>
  </ul>

  <p class="text-color-gray-700 dark:text-color-gray-300">
    Styling things this way contradicts a lot of traditional best practices, but once you try it you’ll quickly notice some really important benefits:
  </p>

  <ul class="list-disc pl-6 space-y-2 text-color-gray-700 dark:text-color-gray-300">
    <li>
      <span class="font-medium text-color-black dark:text-color-white">You get things done faster</span> — you don’t spend any time coming up with class names, making decisions about selectors, or switching between HTML and CSS files, so your designs come together very fast.
    </li>
    <li>
      <span class="font-medium text-color-black dark:text-color-white">Making changes feels safer</span> — adding or removing a utility class to an element only ever affects that element, so you never have to worry about accidentally breaking something on another page that’s using the same CSS.
    </li>
    <li>
      <span class="font-medium text-color-black dark:text-color-white">Maintaining old projects is easier</span> — changing something just means finding that element in your project and changing the classes, not trying to remember how all of that custom CSS works that you haven’t touched in six months.
    </li>
    <li>
      <span class="font-medium text-color-black dark:text-color-white">Your code is more portable</span> — since both the structure and styling live in the same place, you can easily copy and paste entire chunks of UI around, even between different projects.
    </li>
    <li>
      <span class="font-medium text-color-black dark:text-color-white">Your CSS stops growing</span> — since utility classes are so reusable, your CSS doesn’t continue to grow linearly with every new feature you add to a project.
    </li>
  </ul>

  <p class="text-color-gray-700 dark:text-color-gray-300">
    These benefits make a big difference on small projects, but they are even more valuable for teams working on long-running projects at scale.
  </p>
</div>
`
        }
    ]
};

export default styleWithUtilityClasses;

<div className="sr-only">
    <div class="max-w-3xl mx-auto p-6 bg-white shadow rounded-md text-color-gray-800 space-y-6">
  <h2 class="text-2xl font-bold">For example, in the UI above we’ve used:</h2>
  <ul class="list-disc pl-6 space-y-2">
    <li>
      <span class="font-medium">The display and padding utilities</span>
      (<code class=" px-1 rounded">flex</code>, <code class=" px-1 rounded">shrink-0</code>, and <code class=" px-1 rounded">p-6</code>) to control the overall layout
    </li>
    <li>
      <span class="font-medium">The max-width and margin utilities</span>
      (<code class=" px-1 rounded">max-w-sm</code> and <code class=" px-1 rounded">mx-auto</code>) to constrain the card width and center it horizontally
    </li>
    <li>
      <span class="font-medium">The background-color, border-radius, and box-shadow utilities</span>
      (<code class=" px-1 rounded">bg-white</code>, <code class="bg-color-gray-100 px-1 rounded">rounded-xl</code>, and <code class="bg-color-gray-100 px-1 rounded">shadow-lg</code>) to style the card’s appearance
    </li>
    <li>
      <span class="font-medium">The width and height utilities</span>
      (<code class="bg-color-gray-100 px-1 rounded">size-12</code>) to set the width and height of the logo image
    </li>
    <li>
      <span class="font-medium">The gap utilities</span>
      (<code class="bg-color-gray-100 px-1 rounded">gap-x-4</code>) to handle the spacing between the logo and the text
    </li>
    <li>
      <span class="font-medium">The font-size, color, and font-weight utilities</span>
      (<code class="bg-color-gray-100 px-1 rounded">text-xl</code>, <code class="bg-color-gray-100 px-1 rounded">text-black</code>, <code class="bg-color-gray-100 px-1 rounded">font-medium</code>) to style the card text
    </li>
  </ul>

  <p class="text-color-gray-700">
    Styling things this way contradicts a lot of traditional best practices, but once you try it you’ll quickly notice some really important benefits:
  </p>

  <ul class="list-disc pl-6 space-y-2 text-color-gray-700">
    <li>
      <span class="font-medium text-color-black">You get things done faster</span> — you don’t spend any time coming up with class names, making decisions about selectors, or switching between HTML and CSS files, so your designs come together very fast.
    </li>
    <li>
      <span class="font-medium text-black">Making changes feels safer</span> — adding or removing a utility class to an element only ever affects that element, so you never have to worry about accidentally breaking something on another page that’s using the same CSS.
    </li>
    <li>
      <span class="font-medium text-black">Maintaining old projects is easier</span> — changing something just means finding that element in your project and changing the classes, not trying to remember how all of that custom CSS works that you haven’t touched in six months.
    </li>
    <li>
      <span class="font-medium text-black">Your code is more portable</span> — since both the structure and styling live in the same place, you can easily copy and paste entire chunks of UI around, even between different projects.
    </li>
    <li>
      <span class="font-medium text-black">Your CSS stops growing</span> — since utility classes are so reusable, your CSS doesn’t continue to grow linearly with every new feature you add to a project.
    </li>
  </ul>

  <p class="text-color-gray-700">
    These benefits make a big difference on small projects, but they are even more valuable for teams working on long-running projects at scale.
  </p>
</div>

</div>